import type React from "react";
import { authEnabled } from "../AuthGate";
import { SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";
import { formatPrice, productCopy, productMeta, type Product } from "../../sim/payments";
import { PetalsBuyControl, PetalsPriceTag, type PetalsBuy } from "./PetalsBuy";
import { ProductHero, heroPlantName } from "./ProductHero";

/**
 * ONE SHOP TILE — shared by the Shop page and the "products" page-builder block so both render identically.
 *
 * Laid out like a mobile game's shop item, top to bottom, so it reads at a glance in a four-column grid:
 * the hero shot (ProductHero: the live asset over its patterned panel, the badge in the corner), the name,
 * one line of subtitle, then the copy (sim/payments productCopy — a plant is just its two bullets; anything
 * else a two-line pitch and at most two feature ticks), then a footer bar
 * holding the price and the buy buttons. The full description and every feature live on the product page
 * (ProductDetailModal), which the tile opens; a tile never scrolls or grows to fit its copy.
 *
 * `onBuy` starts checkout; `busy`/`enabled` come from the parent. `petals` (from `usePetalsBuy`) adds the
 * Petals price and a "Buy with Petals" control beside it, and says what the player already OWNS: an owned plant
 * shows a disabled "✓ Owned" in place of both buy buttons (the server would refuse the sale anyway). A paused shop
 * (Kickstarter mode) wins over that — every tile reads "Opens at launch".
 *
 * KEYBOARD AND SCREEN READERS: the tile is a plain container, not a role="button" — it holds real buttons, and an
 * interactive element inside another is invalid and reads as one blurred control. The TITLE is the button that opens
 * the product page (Enter and Space, as any button); a mouse click anywhere else on the tile still opens it.
 */
export function ProductCard({ product, onBuy, onOpen, busy, enabled, showPrice = true, petals, paused = false }: {
  product: Product;
  onBuy: (p: Product) => void;
  /** open the larger product-detail view (clicking the card, other than the Buy control). */
  onOpen?: (p: Product) => void;
  busy: boolean;
  enabled: boolean;
  showPrice?: boolean;
  petals?: PetalsBuy;
  /** purchases are paused (Kickstarter mode): the price shows, the buy buttons become one "Opens at launch" */
  paused?: boolean;
}) {
  const m = productMeta(product);
  const copy = productCopy(product, heroPlantName(product));
  const owned = !paused && !!petals?.owns(product);
  return (
    <div
      className={`site-shop-card shop-product prod-card${onOpen ? " clickable" : ""}${owned ? " owned" : ""}`}
      onClick={onOpen ? () => onOpen(product) : undefined}
      data-testid={`prod-card-${product.id}`}
    >
      <ProductHero product={product} className="prod-card-media">
        {m.badge && <span className="prod-card-badge">{m.badge}</span>}
      </ProductHero>
      <div className="prod-card-body">
        {/* title: honours optional per-product style overrides (font / colour / weight) */}
        <div className="site-shop-name" style={titleStyle(m)}>
          {onOpen
            ? <button type="button" className="prod-card-open" title={`View ${product.name} details`}
                onClick={(e) => { e.stopPropagation(); onOpen(product); }}>{product.name}</button>
            : product.name}
        </div>
        {m.subtitle && <div className="prod-card-sub">{m.subtitle}</div>}
        {copy.description && <div className="shop-product-desc">{copy.description}</div>}
        {copy.features.length > 0 && (
          <ul className="prod-card-features">
            {copy.features.slice(0, 2).map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        )}
      </div>
      {showPrice && (
        <div className="shop-product-foot" onClick={(e) => e.stopPropagation()}>
          {/* "$X or N Petals" — the Petals half only where the surface can actually spend them (`petals`
              given), so a card never quotes a price the player has no button for. */}
          <div className="shop-product-prices">
            <span className="shop-product-price" style={m.accent ? { color: m.accent } : undefined}>{formatPrice(product.priceCents, product.currency)}</span>
            {petals && <PetalsPriceTag product={product} orClass="shop-product-or" />}
          </div>
          <div className="shop-product-buys">
            {paused
              ? <button className="site-btn sm" disabled title="The Premium Shop opens with the full game">At full release</button>
              : owned
              ? <button className="site-btn sm prod-owned" disabled title="You own this. It's in your garden.">✓ Owned</button>
              : <>
                <BuyControl product={product} onBuy={onBuy} busy={busy} enabled={enabled} />
                {petals && <PetalsBuyControl product={product} petals={petals} />}
              </>}
          </div>
        </div>
      )}
    </div>
  );
}

/** Build the inline style for the product title from its optional per-product style overrides. */
function titleStyle(m: ReturnType<typeof productMeta>): React.CSSProperties | undefined {
  const s: React.CSSProperties = {};
  if (m.titleFont) s.fontFamily = m.titleFont;
  if (m.titleColor) s.color = m.titleColor;
  if (m.titleWeight) s.fontWeight = m.titleWeight as React.CSSProperties["fontWeight"];
  return Object.keys(s).length ? s : undefined;
}

function BuyControl({ product, onBuy, busy, enabled }: { product: Product; onBuy: (p: Product) => void; busy: boolean; enabled: boolean }) {
  if (!enabled) return <button className="site-btn sm" disabled>Unavailable</button>;
  const btn = <button className="site-btn primary sm" disabled={busy} onClick={() => onBuy(product)}>{busy ? "…" : "Buy"}</button>;
  if (!authEnabled) return btn;
  return (
    <>
      <SignedIn>{btn}</SignedIn>
      <SignedOut><SignInButton mode="modal"><button className="site-btn primary sm">Sign in to buy</button></SignInButton></SignedOut>
    </>
  );
}
